import React, { useState, useRef, useEffect, memo } from 'react';
import apiRequest from "../../services/apiClient";
import { formatDate } from "../../utils/utils";

// Singleton para manejar una sola instancia de SectionDetail
class SectionDetailManager {
  constructor() {
    this.currentRequest = null;
    this.isLoading = false;
    this.listeners = new Set();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify(data) {
    this.listeners.forEach(listener => listener(data));
  }

  async loadSection(selectedItem) {
    if (this.isLoading) {
      console.log('Already loading, ignoring request for:', selectedItem);
      return;
    }

    console.log('Making API request for section:', selectedItem);
    this.isLoading = true;
    
    // Cancelar petición anterior si existe
    if (this.currentRequest) {
      console.log('Cancelling previous request');
      this.currentRequest.cancel?.();
    }
    
    const request = apiRequest(`section/${selectedItem}?$expand=userCreator($select=fullName),userModifier($select=fullName)`);
    this.currentRequest = request;
    
    try {
      const response = await request;
      console.log('API response received for section:', selectedItem, response.data);
      this.notify({ type: 'SUCCESS', data: response.data });
    } catch (error) {
      console.error('Error loading section:', error);
      this.notify({ type: 'ERROR', error });
    } finally {
      this.isLoading = false;
      this.currentRequest = null;
    }
  }

  clear() {
    this.isLoading = false;
    if (this.currentRequest) {
      this.currentRequest.cancel?.();
      this.currentRequest = null;
    }
    this.notify({ type: 'CLEAR' });
  }
}

const sectionDetailManager = new SectionDetailManager();

const SectionDetailSingleton = memo(({ selectedItem, handleUnselectItem, handleEditItem, handleDeleteItem }) => {
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = sectionDetailManager.subscribe(({ type, data, error }) => {
      switch (type) {
        case 'SUCCESS':
          setSection(data);
          setLoading(false);
          break;
        case 'ERROR':
          setLoading(false);
          break;
        case 'CLEAR':
          setSection(null);
          setLoading(false);
          break;
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (selectedItem) {
      setLoading(true);
      sectionDetailManager.loadSection(selectedItem);
    } else {
      sectionDetailManager.clear();
    }
  }, [selectedItem]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!section) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <p className="text-gray-500">No section selected</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={section.name || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="text"
                value={section.backgroundColor || ''}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
              <div 
                className="w-8 h-8 rounded border border-gray-300"
                style={{ backgroundColor: section.backgroundColor }}
              ></div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Font Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="text"
                value={section.fontColor || ''}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
              <div 
                className="w-8 h-8 rounded border border-gray-300"
                style={{ backgroundColor: section.fontColor }}
              ></div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
            <input
              type="text"
              value={formatDate(section.created) || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>

          {section.updated && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Updated</label>
              <input
                type="text"
                value={formatDate(section.updated)}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
          )}

          {section.userCreator && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Creator</label>
              <input
                type="text"
                value={section.userCreator?.fullName || ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
          )}

          {section.userModifier && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Modifier</label>
              <input
                type="text"
                value={section.userModifier?.fullName || ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Identifier</label>
            <input
              type="text"
              value={section.id || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>
        </div>
      </div>

      {/* Fixed Actions */}
      <div className="p-6 pt-4 border-t border-gray-200 bg-white">
        <div className="flex space-x-3">
          <button
            onClick={handleUnselectItem}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleEditItem}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Edit
          </button>
          <button
            onClick={handleDeleteItem}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
});

SectionDetailSingleton.displayName = 'SectionDetailSingleton';

export default SectionDetailSingleton;
